import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { userService } from '../services/userService';
import { User, UserUpdate } from '../types/user';
import { Form, Input, Button, Typography, Card, message, Spin } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';

const { Title } = Typography;

const Profile: React.FC = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [form] = Form.useForm();

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userData = await userService.getCurrentUser();
                setUser(userData);
                form.setFieldsValue({
                    email: userData.email || '',
                    first_name: userData.first_name || '',
                    last_name: userData.last_name || '',
                    password: '',
                    confirmPassword: ''
                });
            } catch (err) {
                message.error(t('profile.errors.loadFailed'));
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [t, form]);

    const onFinish = async (values: UserUpdate & { confirmPassword?: string }) => {
        if (!user) return;

        try {
            setLoading(true);
            const updateData: UserUpdate = {
                id: user.id,
                email: values.email,
                first_name: values.first_name,
                last_name: values.last_name
            };

            if (values.password && values.password.length >= 3) {
                if (values.password !== values.confirmPassword) {
                    form.setFields([
                        {
                            name: 'confirmPassword',
                            errors: [t('profile.errors.passwordsDontMatch')],
                        },
                    ]);
                    return;
                }
                updateData.password = values.password;
            }

            const updatedUser = await userService.updateUser(updateData);
            setUser(updatedUser);
            message.success(t('profile.success.updateSuccess'));
            form.setFieldsValue({ password: '', confirmPassword: '' });
        } catch (err) {
            message.error(t('profile.errors.updateFailed'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!user) {
        return <Typography>{t('profile.userNotFound')}</Typography>;
    }

    return (
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px' }}>
            <Card>
                <Title level={2}>{t('profile.title')}</Title>

                <Form
                    form={form}
                    name="profile"
                    onFinish={onFinish}
                    layout="vertical"
                    requiredMark={false}
                >
                    <Form.Item
                        name="email"
                        label={t('profile.email')}
                        rules={[
                            { required: true, message: t('profile.errors.emailRequired') },
                            { type: 'email', message: t('profile.errors.emailInvalid') }
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined />}
                            placeholder={t('profile.email')}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="first_name"
                        label={t('profile.firstName')}
                        rules={[{ required: true, message: t('profile.errors.firstNameRequired') }]}
                    >
                        <Input
                            prefix={<UserOutlined />}
                            placeholder={t('profile.firstName')}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="last_name"
                        label={t('profile.lastName')}
                        rules={[{ required: true, message: t('profile.errors.lastNameRequired') }]}
                    >
                        <Input
                            prefix={<UserOutlined />}
                            placeholder={t('profile.lastName')}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label={t('profile.password')}
                        help={t('profile.passwordHelper')}
                        rules={[
                            { min: 3, message: t('profile.errors.passwordTooShort') }
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder={t('profile.password')}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('profile.errors.passwordsDontMatch')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder={t('profile.confirmPassword')}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            block
                            loading={loading}
                        >
                            {t('profile.save')}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default Profile; 